export const MAX_TEXTS = 50;
export const MAX_SELECTED_TEXTS = 15;
/** Max senders selectable when applying rule-based presets (matches Presets modal). */
export const MAX_SELECTED_SENDERS = 50;

const RECENT_LEADS_KEY_PREFIX = 'trafficpulse:smswiser:recent-leads';

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function buildRecentLeadsStorageKey(params: { countryKey: string; wiserId: number }): string {
  return `${RECENT_LEADS_KEY_PREFIX}:${params.countryKey}:${params.wiserId}`;
}

export function parseRecentIds(raw: string | null): number[] {
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter((v): v is number => typeof v === 'number' && Number.isFinite(v))
      .slice(0, 12);
  } catch {
    return [];
  }
}

export function uniqueKeepOrderStrings(values: string[]): string[] {
  return Array.from(new Set(values));
}

/** Max length for SMSWiser sender IDs. Values exceeding this are ignored. */
const SENDER_NAME_MAX_LENGTH = 11;

/** Filters sender names to max 11 characters. Longer values are ignored (SMSWiser API limit). */
export function filterSenderNamesMax11(senderNames: string[]): string[] {
  return senderNames.filter((s) => typeof s === 'string' && s.length <= SENDER_NAME_MAX_LENGTH);
}

export function splitLines(value: string): string[] {
  return value
    .split(/[\n,]/g)
    .map((s) => s.trim())
    .filter(Boolean);
}

type ParseSmsTextsResult = { texts: string[]; error: string | null };

function extractCandidateArray(parsed: unknown): unknown[] | null {
  if (Array.isArray(parsed)) return parsed;
  if (!isRecord(parsed)) return null;

  const candidate = parsed.smsTexts ?? parsed.texts ?? parsed.messages;
  return Array.isArray(candidate) ? candidate : null;
}

function normalizeTextCandidate(item: unknown): string {
  if (typeof item === 'string') return item;
  if (!isRecord(item)) return '';

  const v = item.text ?? item.smsText ?? item.message ?? item.value;
  return typeof v === 'string' ? v : '';
}

type ParseSendersResult = { senders: string[]; error: string | null };

function extractSenderArray(parsed: unknown): unknown[] | null {
  if (Array.isArray(parsed)) return parsed;
  if (!isRecord(parsed)) return null;
  const candidate = parsed.senderNames ?? parsed.senders ?? parsed.names;
  return Array.isArray(candidate) ? candidate : null;
}

function normalizeSenderCandidate(item: unknown): string {
  if (typeof item === 'string') return item;
  if (!isRecord(item)) return '';
  const v = item.name ?? item.sender ?? item.value;
  return typeof v === 'string' ? v : '';
}

export function parseSenderNamesInput(value: string): ParseSendersResult {
  const raw = String(value || '').trim();
  if (!raw) return { senders: [], error: null };

  const looksLikeJson = raw.startsWith('[') || raw.startsWith('{');
  if (!looksLikeJson) return { senders: filterSenderNamesMax11(splitLines(raw)), error: null };

  try {
    const parsed = JSON.parse(raw) as unknown;
    const candidate = extractSenderArray(parsed);
    if (!candidate) return { senders: [], error: 'JSON must be an array or object with valid key.' };
    const rawSenders = candidate
      .map(normalizeSenderCandidate)
      .map(String)
      .map((s) => s.trim())
      .filter(Boolean);
    const senders = filterSenderNamesMax11(rawSenders);
    return senders.length ? { senders, error: null } : { senders: [], error: 'No senders found in JSON (max 11 chars each).' };
  } catch {
    return { senders: [], error: "It's not valid JSON." };
  }
}

export function parseSmsTextsInput(value: string): ParseSmsTextsResult {
  const raw = String(value || '').trim();
  if (!raw) return { texts: [], error: null };

  const looksLikeJson = raw.startsWith('[') || raw.startsWith('{');
  if (!looksLikeJson) return { texts: splitLines(raw), error: null };

  try {
    const parsed = JSON.parse(raw) as unknown;
    const candidate = extractCandidateArray(parsed);

    if (!candidate) return { texts: [], error: 'JSON must be an array or object with valid key.' };

    const texts = candidate
      .map(normalizeTextCandidate)
      .map(String)
      .map((t) => t.trim())
      .filter(Boolean);

    return texts.length ? { texts, error: null } : { texts: [], error: 'No texts found in JSON.' };
  } catch {
    return { texts: [], error: "It's not right JSON." };
  }
}
